// file: src/hooks/useDragWidth.js
import { useState, useEffect, useRef } from 'react';

export default function useDragWidth(
  ref,
  {
    defaultWidth = 300,
    minWidth = 200,
    maxWidth = 600,
    invertDrag = false,
  }
) {
  const [dragging, setDragging] = useState(false);
  const dragState = useRef({
    startX: 0,
    startWidth: defaultWidth,
    currentWidth: defaultWidth,
    lastAnimationFrame: null
  });

  useEffect(() => {
    // Set the initial width and prepare for GPU acceleration
    if (ref.current) {
      ref.current.style.width = `${defaultWidth}px`;
      ref.current.style.willChange = 'transform';
      ref.current.style.transform = 'translateZ(0)';
    }
  }, [defaultWidth, ref]);

  function handlePointerDown(e) {
    e.preventDefault();
    setDragging(true);
    
    // Support both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
    
    dragState.current = {
      startX: clientX,
      startWidth: ref.current ? ref.current.offsetWidth : defaultWidth,
      currentWidth: ref.current ? ref.current.offsetWidth : defaultWidth,
      lastAnimationFrame: null
    };
    
    // Disable text selection during drag (but not pointer events to allow touch)
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.touchAction = 'none';
  }

  function handlePointerMove(e) {
    if (!dragging) return;
    e.preventDefault();

    // Cancel any existing animation frame
    if (dragState.current.lastAnimationFrame) {
      cancelAnimationFrame(dragState.current.lastAnimationFrame);
    }

    dragState.current.lastAnimationFrame = requestAnimationFrame(() => {
      if (!ref.current || !dragging) return;

      // Support both mouse and touch events
      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
      const deltaX = clientX - dragState.current.startX;
      
      let newWidth = invertDrag
        ? dragState.current.startWidth - deltaX
        : dragState.current.startWidth + deltaX;

      // Constrain width
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      
      if (newWidth !== dragState.current.currentWidth) {
        dragState.current.currentWidth = newWidth;
        ref.current.style.width = `${newWidth}px`;
      }
    });
  }

  function handlePointerUp() {
    if (!dragging) return;
    
    setDragging(false);
    
    // Re-enable text selection and touch
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.touchAction = '';

    // Cancel any pending animation frame
    if (dragState.current.lastAnimationFrame) {
      cancelAnimationFrame(dragState.current.lastAnimationFrame);
      dragState.current.lastAnimationFrame = null;
    }
  }

  useEffect(() => {
    if (!dragging) return;

    // Use passive listeners where possible for better mobile performance
    const moveOptions = { passive: false };
    const upOptions = { passive: true };

    // Support both mouse and touch events
    window.addEventListener('mousemove', handlePointerMove, moveOptions);
    window.addEventListener('mouseup', handlePointerUp, upOptions);
    window.addEventListener('touchmove', handlePointerMove, moveOptions);
    window.addEventListener('touchend', handlePointerUp, upOptions);
    window.addEventListener('touchcancel', handlePointerUp, upOptions);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove, moveOptions);
      window.removeEventListener('mouseup', handlePointerUp, upOptions);
      window.removeEventListener('touchmove', handlePointerMove, moveOptions);
      window.removeEventListener('touchend', handlePointerUp, upOptions);
      window.removeEventListener('touchcancel', handlePointerUp, upOptions);
      
      // Cleanup on unmount
      if (dragState.current.lastAnimationFrame) {
        cancelAnimationFrame(dragState.current.lastAnimationFrame);
      }
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.touchAction = '';
    };
  }, [dragging]);

  // Return handler that works with both mouse and touch
  const handleStart = (e) => {
    // Detect if it's a touch event
    if (e.type === 'touchstart') {
      handlePointerDown(e);
    } else {
      handlePointerDown(e);
    }
  };

  return {
    handleMouseDown: handleStart,
    handleTouchStart: handleStart,
    dragging,
  };
}


